import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Observable, of, Subscription, throwError } from "rxjs";
import { finalize, map, tap } from "rxjs/operators";
import { FeedbackAnswerPayload, FeedbackTemplateQuestion, FeedbackTemplateSnapshot } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import {
	feedbackQuestionAnswerRequired,
	feedbackQuestionNeedsAnswer,
	feedbackQuestionNeedsRating,
} from "../../feedback/feedback-question.utils";

function requiredTrimmedText(control: AbstractControl): ValidationErrors | null {
	return String(control.value ?? "").trim().length > 0 ? null : { required: true };
}

@Component({
	selector: "app-checklist-feedback",
	templateUrl: "./checklist-feedback.component.html",
	styleUrls: ["./checklist-feedback.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ChecklistFeedbackComponent implements OnChanges, OnDestroy {
	@Input() template: FeedbackTemplateSnapshot | null = null;
	@Input() taskId = "";
	@Input() submitted = false;
	@Input() embedded = false;
	@Output() completed = new EventEmitter<void>();
	@Output() back = new EventEmitter<void>();
	@Output() formValidChange = new EventEmitter<boolean>();

	form: FormGroup;
	submitting = false;
	submitError = "";
	pageIndex = 0;
	readonly pageSize = 10;

	private formStatusSub: Subscription | null = null;
	private submitSub: Subscription | null = null;

	constructor(
		private fb: FormBuilder,
		private httpService: HttpService,
		private loaderService: LoaderService,
	) {
		this.form = this.fb.group({ answers: this.fb.array([]) });
	}

	ngOnChanges(): void {
		this.buildForm();
	}

	ngOnDestroy(): void {
		this.formStatusSub?.unsubscribe();
		this.submitSub?.unsubscribe();
	}

	get questions() {
		return [...(this.template?.questions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
	}

	get pagedQuestions() {
		const start = this.pageIndex * this.pageSize;
		return this.questions.slice(start, start + this.pageSize);
	}

	get totalPages(): number {
		return Math.max(1, Math.ceil(this.questions.length / this.pageSize));
	}

	get answers(): FormArray {
		return this.form.get("answers") as FormArray;
	}

	get isFormValid(): boolean {
		return this.submitted || this.form.valid;
	}

	needsRating(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionNeedsRating(question);
	}

	needsAnswer(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionNeedsAnswer(question);
	}

	answerRequired(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionAnswerRequired(question);
	}

	answerGroup(index: number): FormGroup {
		return this.answers.at(index) as FormGroup;
	}

	globalQuestionIndex(pageLocalIndex: number): number {
		return this.pageIndex * this.pageSize + pageLocalIndex;
	}

	questionDisplayNumber(pageLocalIndex: number): number {
		return this.globalQuestionIndex(pageLocalIndex) + 1;
	}

	goBack(): void {
		this.back.emit();
	}

	prevPage(): void {
		this.pageIndex = Math.max(0, this.pageIndex - 1);
	}

	nextPage(): void {
		this.pageIndex = Math.min(this.totalPages - 1, this.pageIndex + 1);
	}

	submitFeedback(): void {
		this.submitError = "";
		this.submitSub?.unsubscribe();
		this.submitSub = this.submit().subscribe({
			error: (err: { message?: string }) => {
				this.submitError = err?.message || "Unable to submit feedback.";
			},
		});
	}

	submit(): Observable<void> {
		if (this.submitted) {
			return of(undefined);
		}
		if (!this.taskId || this.submitting) {
			return throwError(() => new Error("Feedback submission is not available."));
		}
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return throwError(() => new Error("Feedback form is invalid."));
		}

		const payload: FeedbackAnswerPayload[] = this.answers.controls.map((ctrl, index) => {
			const question = this.questions[index];
			const answer: FeedbackAnswerPayload = {
				questionId: ctrl.get("questionId")?.value,
			};
			if (feedbackQuestionNeedsRating(question)) {
				answer.rating = Number(ctrl.get("rating")?.value);
			}
			if (feedbackQuestionNeedsAnswer(question)) {
				const remarks = String(ctrl.get("remarks")?.value ?? "").trim();
				if (remarks) answer.remarks = remarks;
			}
			return answer;
		});

		this.submitting = true;
		this.loaderService.setLoading(true);

		return this.httpService.submitFeedback({ taskId: this.taskId, answers: payload }).pipe(
			tap(() => this.completed.emit()),
			map(() => undefined),
			finalize(() => {
				this.submitting = false;
				this.loaderService.setLoading(false);
			}),
		);
	}

	private buildForm(): void {
		this.formStatusSub?.unsubscribe();

		const answers = this.fb.array(
			this.questions.map((q) =>
				this.fb.group({
					questionId: [q.id],
					rating: [
						null,
						feedbackQuestionNeedsRating(q) ? [Validators.required, Validators.min(1), Validators.max(5)] : [],
					],
					remarks: ["", feedbackQuestionAnswerRequired(q) ? [requiredTrimmedText] : []],
				}),
			),
		);
		this.form.setControl("answers", answers);
		this.pageIndex = 0;

		this.emitFormValid();
		this.formStatusSub = this.form.statusChanges.subscribe(() => this.emitFormValid());
	}

	private emitFormValid(): void {
		this.formValidChange.emit(this.isFormValid);
	}
}
